// @ts-strict-ignore
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { useAtomValue, useSetAtom } from 'jotai'
import { useTranslation } from 'react-i18next'
import { useDebounce } from '@node-space/hooks'
import { ContactPaymentInstrument } from '@node-space/types'
import { Box, ChipList, Input, NoResults, RowsSkeletonLoader } from '@node-space/ui-components'
import { SEARCH_INPUT_DEBOUNCE_TIME } from 'constants/General'
import {
  ContactBankAccountFilters,
  useContactBankAccounts,
} from 'hooks/fiat/useContactBankAccounts'
import { mapBankAccountPaymentInstrumentToBeneficiaryDetails } from 'pages/Contacts/utils/fiatInstruments'
import { BeneficiaryEntityType } from 'types/beneficiaries'
import { ContactPaymentInstruments, RelationshipType } from 'types/contacts'
import { FiatSendStep, FiatSendSteps } from 'types/walletActions'
import {
  fiatSendFormAtom,
  selectedContactBankAccountAtom,
  selectedFiatBeneficiaryAtom,
  selectedWalletAtom,
} from '../../Pay/atoms/atoms'
import { PayoutBeneficiaryFilters } from '../constants'
import useBeneficiaryFormContext from '../hooks/useBeneficiaryFormContext'
import { useWalletBeneficiaryFilters } from '../hooks/useWalletBeneficiaryFilters'
import { useWalletOptions } from '../hooks/useWalletOptions'
import { InstrumentList } from '../InstrumentList'
import { getPaymentMethodsForCurrency } from '../utils'
import { ErrorStateDisplay } from './shared/ErrorStateDisplay'
import { WalletSelector } from './shared/WalletSelector'

interface ContactBankAccountFormProps {
  setStep: React.Dispatch<React.SetStateAction<FiatSendStep>>
  isLoadingWallet: boolean
}

const ContactBankAccountForm = ({ setStep, isLoadingWallet }: ContactBankAccountFormProps) => {
  const { t } = useTranslation()

  const sourceWalletAtom = useAtomValue(selectedWalletAtom)
  const setBeneficiaryAtom = useSetAtom(selectedFiatBeneficiaryAtom)
  const setContactBankAccountAtom = useSetAtom(selectedContactBankAccountAtom)
  const formAtom = useAtomValue(fiatSendFormAtom)

  const [showErrorState, _setShowErrorState] = useState(false)
  const [searchQuery, setSearchQuery] = useState<string>('')
  const [contactFilters, setContactFilters] = useState<ContactBankAccountFilters>()

  const debouncedSearchTerm: string = useDebounce(searchQuery, SEARCH_INPUT_DEBOUNCE_TIME)

  const { errors, clearErrors, register, setValue, setError, resetField } = formAtom || {}

  const {
    isLoading: isFetchingWalletFilters,
    filters,
    capabilities,
  } = useWalletBeneficiaryFilters({
    wallet: sourceWalletAtom,
    isContactBankAccountFilters: true,
  })

  const { isInsufficientBalance, isPayoutsSupported } = useBeneficiaryFormContext({
    wallet: sourceWalletAtom,
    filters,
  })

  const getPaymentMethodsForWallet = useCallback(() => {
    if (!sourceWalletAtom?.currency) return []
    return getPaymentMethodsForCurrency(
      sourceWalletAtom.currency,
      sourceWalletAtom.supportedTransferDestinations
    )
  }, [sourceWalletAtom?.currency, sourceWalletAtom?.supportedTransferDestinations])

  const contactBankAccountFilters = useMemo(
    () => ({
      currency: sourceWalletAtom?.currency,
      searchTerm: debouncedSearchTerm,
      paymentMethods: getPaymentMethodsForWallet(),
      ...contactFilters,
    }),
    [sourceWalletAtom?.currency, debouncedSearchTerm, contactFilters]
  )

  const {
    contactBankAccounts,
    fetchNextPage,
    hasNextPage,
    isFetching: isFetchingContactBankAccounts,
    isFetchingNextPage,
  } = useContactBankAccounts({
    walletLsid: sourceWalletAtom?.walletId || '',
    filters: contactBankAccountFilters,
    capabilities,
  })

  const { walletsOptions } = useWalletOptions({
    sourceWalletAtom: sourceWalletAtom,
  })

  useEffect(() => {
    resetField('amount')
    resetField('fee')
    resetField('paymentReference')
    resetField('beneficiaryReference')
  }, [])

  const isLoadingWalletOptions = useMemo(() => {
    return isLoadingWallet
  }, [isLoadingWallet])

  const handleContactBankAccountSelect = useCallback(
    (contactPaymentInstrument: ContactPaymentInstruments) => {
      if (!sourceWalletAtom) {
        setError('sourceWalletLsid', { message: t('wallets.fiatSend.noWalletSelectedError') })
        return
      }
      clearErrors()

      const { paymentInstrument } = contactPaymentInstrument || {}
      const beneficiary =
        mapBankAccountPaymentInstrumentToBeneficiaryDetails(contactPaymentInstrument)

      setContactBankAccountAtom(contactPaymentInstrument)
      setValue('beneficiaryReference', beneficiary?.reference)
      setValue('paymentInstrumentId', paymentInstrument?.id)
      setValue('currencyCode', beneficiary?.currency)
      setValue('transferDestination', beneficiary?.transferDestination)
      setBeneficiaryAtom(beneficiary)
      setValue('paymentMethod', '')
      setValue('achSameDay', false)
      setStep(FiatSendSteps.DETAILS)
    },
    [sourceWalletAtom, setError, clearErrors, setValue, setBeneficiaryAtom, setStep, t]
  )

  const onHandleBankAccountSearch = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchQuery(e.target.value)
    },
    [setSearchQuery]
  )

  const filtersDisabled = useMemo(() => {
    return (
      !isLoadingWalletOptions &&
      !isFetchingWalletFilters &&
      (!isPayoutsSupported || isInsufficientBalance)
    )
  }, [isLoadingWalletOptions, isFetchingWalletFilters, isPayoutsSupported, isInsufficientBalance])

  const showCallout = useMemo(() => {
    return !isLoadingWalletOptions && isInsufficientBalance
  }, [isLoadingWalletOptions, isInsufficientBalance])

  const handleFilterChange = useCallback(
    (selectedFilter: string[]) => {
      const selectedFilterValue = selectedFilter?.[0]

      const filterUpdates: Partial<ContactBankAccountFilters> = {
        searchTerm: debouncedSearchTerm,
      }

      if (selectedFilterValue === PayoutBeneficiaryFilters.BUSINESS) {
        filterUpdates.entityType = BeneficiaryEntityType.COMPANY
        filterUpdates.relationshipType = RelationshipType.THIRD_PARTY
      } else if (selectedFilterValue === PayoutBeneficiaryFilters.INDIVIDUAL) {
        filterUpdates.entityType = BeneficiaryEntityType.INDIVIDUAL
        filterUpdates.relationshipType = RelationshipType.THIRD_PARTY
      } else if (selectedFilterValue === PayoutBeneficiaryFilters.MY_COMPANY) {
        filterUpdates.entityType = BeneficiaryEntityType.COMPANY
        filterUpdates.relationshipType = RelationshipType.SELF_OWNED
      }

      setContactFilters(filterUpdates as ContactBankAccountFilters)
    },
    [sourceWalletAtom, debouncedSearchTerm, getPaymentMethodsForWallet]
  )

  return (
    <Box paddingT={24} className="max-h-[75vh]">
      {showErrorState && <ErrorStateDisplay />}
      {!showErrorState && (
        <Box paddingB={16}>
          <WalletSelector
            register={register('sourceWalletLsid')}
            sourceWalletAtom={sourceWalletAtom}
            walletsOptions={walletsOptions}
            isLoadingWalletOptions={isLoadingWalletOptions}
            errorText={errors?.sourceWalletLsid?.message}
          />

          {sourceWalletAtom && (
            <>
              <Box paddingY={16} width="full" justify="between" flex gapY={12} gapX={12}>
                <Box width="full">
                  <Input
                    placeholderText={t('wallets.fiatSend.search')}
                    onChange={onHandleBankAccountSearch}
                    searchable={true}
                    testId="fiat-send-contact-search"
                    helperText={t('payouts.contactFiltersDisclaimer')}
                    disabled={filtersDisabled}
                  />
                </Box>
              </Box>

              {isFetchingWalletFilters ? (
                <RowsSkeletonLoader rows={1} />
              ) : (
                filters?.length > 1 &&
                !showCallout && (
                  <Box flex className="mb-4" gapX={12}>
                    <span className="text-body-regular text-foreground-prominent">Filter by:</span>
                    <ChipList
                      key={sourceWalletAtom?.walletId}
                      multiselect={false}
                      chips={filters}
                      onHandleChange={handleFilterChange}
                    />
                  </Box>
                )
              )}

              {(isFetchingContactBankAccounts || isFetchingWalletFilters) && (
                <RowsSkeletonLoader rows={5} />
              )}

              {!isFetchingContactBankAccounts &&
                !isFetchingWalletFilters &&
                !!contactBankAccounts?.length && (
                  <Box className="w-full">
                    <InstrumentList
                      handleBankAccountSelect={handleContactBankAccountSelect}
                      isFetchingNextPage={isFetchingNextPage}
                      contacts={contactBankAccounts as ContactPaymentInstrument[]}
                      hasLoadedMore={true}
                      fetchNextPage={fetchNextPage}
                      setHasLoadedMore={() => {}}
                      hasNextPage={hasNextPage}
                    />
                  </Box>
                )}

              {!isFetchingContactBankAccounts &&
                !isFetchingWalletFilters &&
                !contactBankAccounts?.length && (
                  <NoResults
                    dataName="contact bank accounts"
                    description={t('contacts.noContactBankAccountsDescription')}
                  />
                )}
            </>
          )}
        </Box>
      )}
    </Box>
  )
}

export default memo(ContactBankAccountForm)
